/* eslint-disable no-unused-vars */
import { Rho } from '../lib/rhojs';
import { componentsLoader } from '../lib/masters/src/componentsLoader';
import prepareSvg from './ptUpdate';
import Vue from 'vue';
import Attachment, { AttachmentName, AttachmentType } from '../../../classes/Attachment';
import marked from 'marked';
import PServiceContent from '../../../services/PServiceContent';
import { getLibraryId } from '../../../utils';
import { DatasheetConfigGroups, DatasheetFilterShowLevel } from '../../../classes/datasheet/groups';
import { openContentWhenClick } from './openContentWhenClick';
import { DiagramTools } from '../../../models/diagramTools';

const components = componentsLoader();

export const CONDITION_FILE_NAME = 'condition.json';
export const NO_CONTENT_RELATED_MESSAGE = 'No content related';

export function createDiagramController(vm) {
	return new Vue({
		data() {
			return {
				diagram: false,
				current_condition_id: '',
				current_condition_changed: false,
				selectedTool: DiagramTools.point,
				domElement: null,
				pan: null,
				zoom: 1,
				highlighted: [],
				leftPanelSelected: -1,
				rightDrawerStatus: false,
				selected: [],
				datasheet: false,
				relatedNotes: false,
				tag: false,
				datasheetFilter: {},
				datasheetPrefix: '',
				datasheetPrefixExceptionsRegexps: [],
				isContextMenuOpen: false,
				isDiagramInitialized: false,
				isPanZoomCentered: false,
				isResettingPanZoom: false,
				diagramPanDebouncer: null
			};
		},
		created() {
			this.$setDefaultContentValues();
			this.$fixShapesWithDots(); // #10904 issue with "." in shapes when deleting draft

			if (!this.view) {
				this.$setDefaultDatasheetFilterConfigGroups();

				return;
			}

			this.$setDatasheetFilterFrom(this.view);
			this.$setLeftPanelSelectedFrom(this.view);
			this.$setPanFrom(this.view);
			this.$setZoomFrom(this.view);
			this.$setPanZoomCenteredFrom(this.view);
		},
		methods: {
			//----------------------- Public interface -------------------
			init(domElement) {
				this.domElement = domElement;
				if (this.svg_attachment) this.$refresh();
			},
			async changeCondition(condition, editMode) {
				if (!condition) return;

				try {
					const conditionConfig = await this.$loadConditionConfig(condition.url);

					this.$applyConditionConfigToDiagram(conditionConfig);
					this.$handleConditionChange(condition, editMode);
				} catch (err) {
					console.log(err);
					this.$handleConditionChange(condition, editMode);
				}
			},
			zoomIn() {
				this.diagram.panZoom.zoomIn();
			},
			zoomOut() {
				this.diagram.panZoom.zoomOut();
			},
			zoomReset() {
				this.isResettingPanZoom = true;
				this.isPanZoomCentered = true;

				try {
					this.$resetPanAndZoom();
					this.$emitCurrentView();
				} finally {
					this.isResettingPanZoom = false;
				}
			},
			setElementProp(id, prop, value) {
				this.diagram.model[id][prop] = value;
				this.current_condition_changed = true;
				this.isContextMenuOpen = false;
			},
			getElementProp(id, prop) {
				return this.diagram.model[id][prop];
			},
			saveCurrentConditionMetadata({ title, description }) {
				this.current_condition.metadata = { title, description };
			},
			removeCurrentCondition() {
				const currentConditionId = this.current_condition?._id;
				const conditionIndexToRemove = this.value.body.attachments.findIndex(
					attachment => currentConditionId === attachment._id
				);

				if (conditionIndexToRemove !== -1) this.value.body.attachments.splice(conditionIndexToRemove, 1);

				this.$applyDefaultCondition();

				this.current_condition_changed = false;
			},
			saveCurrentCondition() {
				const file = new File([JSON.stringify(this.diagram.model)], CONDITION_FILE_NAME);
				const new_id = Math.random();

				this.current_condition.file = file;
				this.current_condition.url = URL.createObjectURL(file);
				this.current_condition._id = new_id;

				this.current_condition_id = new_id;
				this.current_condition_changed = false;
			},
			saveAsNewCondition(title) {
				const newConditionFile = new File([JSON.stringify(this.diagram.model)], CONDITION_FILE_NAME);

				const newConditionAttachment = {
					_id: Math.random(),
					attachment_type: `.${AttachmentType.json}`,
					file: newConditionFile,
					metadata: { title, description: '' },
					name: 'condition',
					url: URL.createObjectURL(newConditionFile)
				};

				this.attachments.push(newConditionAttachment);
				this.changeCondition(newConditionAttachment);

				this.current_condition_changed = false;
			},
			setVisio(file) {
				this.$addNewAttachmentFrom(file, AttachmentName.vsdx, AttachmentType.vsdx);
			},
			setSvg(file) {
				this.$addNewAttachmentFrom(file, AttachmentName.svg, AttachmentType.svg);

				this.$refresh();
			},
			toggleRightDrawer() {
				this.rightDrawerStatus = !this.rightDrawerStatus;
			},
			addNotesToShape(id, notes) {
				if (!this.value.body.contents.shapes[id]) {
					this.$set(this.value.body.contents.shapes, id, {});
				}

				if (!this.value.body.contents.shapes[id].notes) {
					this.$set(this.value.body.contents.shapes[id], 'notes', '');
				}

				this.value.body.contents.shapes[id].notes = notes;
			},
			addLinkToShape(id, link) {
				if (!this.value.body.contents.shapes[id]) {
					this.$set(this.value.body.contents.shapes, id, {});
				}

				if (!this.value.body.contents.shapes[id].links) {
					this.$set(this.value.body.contents.shapes[id], 'links', []);
				}

				this.value.body.contents.shapes[id].links = [];
				this.value.body.contents.shapes[id].links.push(link);
			},
			setTag(tag) {
				this.tag = tag;
			},
			findDatasheet(tag, id) {
				this.datasheet = false;

				const normalizedTag = this.$normalizeDatasheetTag(tag);

				this.setTag(normalizedTag);

				this.$nextTick(() => {
					if (normalizedTag) {
						this.$loadDatasheet(normalizedTag);
						return;
					}

					this.$showShapeNotes(id);
				});
			},
			setNotes(notes) {
				this.relatedNotes = notes;
			},
			async goToLink(id) {
				if (!this.shapes[id]?.links?.[0]?.target) return;

				const link = this.shapes[id].links[0];
				const libraryId = link.library_id ?? getLibraryId(this.value);

				PServiceContent.openContentInNewTab(
					await PServiceContent.getContentByCustomId({
						custom_id: link.target,
						library_id: libraryId,
						isLink: true
					})
				);
			},
			highlight(currentTarget) {
				this.diagram.highlight(currentTarget);
				this.$emitCurrentView();
			},
			//----------------------- ------------------- -------------------
			$setDefaultContentValues() {
				if (!this.value.body.contents) {
					this.$set(this.value.body, 'contents', {});
				}

				if (!this.value.body.contents.shapes) {
					this.$set(this.value.body.contents, 'shapes', {});
				}

				if (!this.value.body.contents.settings) {
					this.$set(this.value.body.contents, 'settings', {
						datasheet: {
							defaultView: 'table'
						}
					});
				}
			},
			$fixShapesWithDots() {
				if (!this.value.body.contents.shapes) return;

				for (const shapeId in this.value.body.contents.shapes) {
					if (shapeId.includes('.')) {
						delete this.value.body.contents.shapes[shapeId];
					}
				}
			},
			$setDefaultDatasheetFilterConfigGroups() {
				DatasheetConfigGroups.forEach(group => {
					this.$set(this.datasheetFilter, group.value, DatasheetFilterShowLevel.all);
				});
			},
			$setDatasheetFilterFrom(view) {
				if (view.datasheetFilter) {
					this.datasheetFilter = view.datasheetFilter;
				}
			},
			$setLeftPanelSelectedFrom(view) {
				if (isNaN(view.leftDrawerIndex)) return;

				this.leftPanelSelected = view.leftDrawerIndex ?? -1;
			},
			$setPanFrom(view) {
				if (view.panZoom && view.panZoom.pan) {
					this.pan = view.panZoom.pan;
				}
			},
			$setZoomFrom(view) {
				if (view.panZoom && view.panZoom.zoom) {
					this.zoom = view.panZoom.zoom;
				}
			},
			$setPanZoomCenteredFrom(view) {
				if (view.isPanZoomCentered) {
					this.isPanZoomCentered = view.isPanZoomCentered;
				}
			},
			async $refresh() {
				await this.$renderSvg();

				const svgElement = this.$getSvgElementFullSized();
				prepareSvg(svgElement);

				this.$initializeDiagram(svgElement);
				await this.$configureDiagram(svgElement);

				this.isDiagramInitialized = true;
			},
			async $renderSvg() {
				const namespacedClass = this.$getNamespacedClass();
				const svgDiagram = await this.$loadSvg(namespacedClass);

				this.domElement.innerHTML = svgDiagram;
				this.domElement.classList.add(namespacedClass);
			},
			async $loadSvg(namespacedClass) {
				const response = await fetch(this.svg_attachment.url);
				const svg = await response.text();

				return this.$setNamespacedClass(svg, namespacedClass);
			},
			$getNamespacedClass() {
				return 'pt-svg-' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
			},
			$setNamespacedClass(svg, namespaced_class) {
				return svg.replace(/<style type="text\/css">([\s\S]*)<\/style>/, (match, css) => {
					const newcss = css.replace(/(\.st[0-9]*)/g, `.${namespaced_class} $1`);
					// eslint-disable-next-line no-useless-escape
					return `<style type="text\/css">${newcss}<\/style>`;
				});
			},
			$getSvgElementFullSized() {
				const svg = this.domElement.querySelector('svg');
				svg.setAttribute('width', '100%');
				svg.setAttribute('height', '100%');
				return svg;
			},
			$initializeDiagram(svgElement) {
				this.diagram = new Rho(svgElement, {
					components,
					zoom: true,
					globalEventListener: async (evt, master, $model) => {
						await openContentWhenClick(evt);
						this.$emit(evt.type, { evt, master, $model });
					}
				});
			},
			async $configureDiagram(svgElement) {
				this.$setPanZoomListeners();
				this.$setDatasheetPrefixAndPrefixExceptions(svgElement);
				this.$showAllLayers();

				await this.$applyView();

				this.$selectSvgComponents();
			},
			$setPanZoomListeners() {
				this.diagram.panZoom.setOnPan(newPan => {
					this.pan = newPan;
					if (this.isResettingPanZoom) return;

					this.isPanZoomCentered = false;

					if (this.diagramPanDebouncer) {
						clearTimeout(this.diagramPanDebouncer);
					}

					this.diagramPanDebouncer = setTimeout(() => {
						this.$emitCurrentView();
					}, 200);
				});

				this.diagram.panZoom.setOnZoom(newZoom => {
					this.zoom = newZoom;
					this.pan = this.diagram.panZoom.getPan();
					if (this.isResettingPanZoom) return;

					this.isPanZoomCentered = false;
					this.$emitCurrentView();
				});
			},
			$setDatasheetPrefixAndPrefixExceptions(svg) {
				try {
					const { dataset } = svg.querySelector('g > g');
					const { datasheetPrefix, datasheetPrefixExceptions } = dataset;

					this.datasheetPrefix = datasheetPrefix || '';
					this.datasheetPrefixExceptionsRegexps = datasheetPrefixExceptions
						? this.$createPrefixRegExpsFrom(datasheetPrefixExceptions)
						: [];
				} catch (e) {
					// Do nothing if an error is thrown
				}
			},
			$createPrefixRegExpsFrom(exceptions) {
				return exceptions.split(',').map(prefix => new RegExp(`^${prefix.trim()}`));
			},
			$showAllLayers() {
				//fix: visio write display:none as default in some diagrams
				Object.keys(this.diagram.layers).forEach(layerName => {
					this.diagram.layers[layerName].show();
				});
			},
			async $applyView() {
				if (!this.view) {
					this.$applyDefaultCondition();
					return;
				}

				this.$applySelectionFrom(this.view);
				this.$applyHighlightedFrom(this.view);
				this.$applyLayersFrom(this.view);
				this.$applyLeftDrawerFrom(this.view);
				this.$applyPanZoomFrom(this.view);
				this.$applyPanZoomCenteredFrom(this.view);
				await this.$applyConditionFrom(this.view);
			},
			$applyDefaultCondition() {
				if (this.conditions.length > 0 && !this.current_condition) {
					this.changeCondition(this.conditions[0]);
				}
			},
			$applySelectionFrom(view) {
				this.selected = (view.selected ?? []).filter(selectedElement =>
					Boolean(document.getElementById(selectedElement))
				);
			},
			$applyHighlightedFrom(view) {
				view.highlighted?.forEach(e => {
					const svgComponentHighlighted = document.getElementById(e);
					this.diagram.highlight(svgComponentHighlighted);
				});
			},
			$applyLayersFrom(view) {
				if (view.layers) {
					this.$setDiagramLayers();
				}
			},
			$setDiagramLayers() {
				const { layers: viewLayers } = this.view;
				const { layers: diagramControllerLayers } = this.diagram;

				Object.entries(viewLayers).forEach(([layerName, viewLayer]) => {
					const diagramControllerLayer = diagramControllerLayers[layerName];

					if (!diagramControllerLayer) return;

					if (viewLayer.visible !== diagramControllerLayer.visible) {
						viewLayer.visible ? diagramControllerLayer.show() : diagramControllerLayer.hide();
					}

					if ('opacity' in viewLayer) {
						diagramControllerLayer.setOpacity(viewLayer.opacity);
					}
				});
			},
			$applyLeftDrawerFrom(view) {
				const { leftDrawer, leftDrawerIndex } = view;

				if (leftDrawer) {
					// retrocompatibilidad
					this.leftPanelSelected = 0;
				}
				if (!isNaN(leftDrawerIndex)) {
					this.leftPanelSelected = leftDrawerIndex;
				}
			},
			$applyPanZoomFrom(view) {
				if (view.panZoom) {
					this.$setPanZoom(view.panZoom);
				}
			},
			$setPanZoom(panZoom) {
				const { pan, zoom } = panZoom;
				this.$setZoom(zoom);
				this.$setPan(pan);
			},
			$setPan(pan) {
				if (pan && pan.x !== null && pan.y !== null) {
					this.diagram.panZoom.pan({ x: pan.x, y: pan.y });
				}
			},
			$setZoom(zoom) {
				this.diagram.panZoom.zoom(zoom);
			},
			$applyPanZoomCenteredFrom(view) {
				if (!view.isPanZoomCentered) return;

				this.$resetPanAndZoom();
			},
			$resetPanAndZoom() {
				this.diagram.panZoom.resize();
				this.diagram.panZoom.fit();
				this.diagram.panZoom.center();
				this.diagram.panZoom.reset();
			},
			async $applyConditionFrom(view) {
				const { condition_title, condition_id } = view;

				let condition;

				if (condition_title) {
					condition = this.conditions.find(({ metadata }) => metadata.title === condition_title);
				} else if (condition_id) {
					condition = this.conditions.find(({ _id }) => _id === condition_id);
				}

				await this.changeCondition(condition || this.conditions[0]);
			},
			$selectSvgComponents() {
				this.selected.forEach(id => {
					const svgComponent = document.getElementById(id);

					if (!svgComponent) return;

					this.diagram.select(
						svgComponent,
						() => this.$handleSvgComponentSelect(id, svgComponent),
						() => this.$handleSvgComponentHover(svgComponent)
					);
				});
			},
			$handleSvgComponentSelect(id, svgComponent) {
				const index = this.selected.indexOf(id);

				if (index > -1) this.selected.splice(index, 1);

				const tag = svgComponent.dataset.tag;
				this.findDatasheet(tag, id);
			},
			$handleSvgComponentHover(svgComponent) {
				const event = new CustomEvent('diagram-tag-hover', { detail: { target: svgComponent } });
				document.dispatchEvent(event);
			},
			async $loadConditionConfig(url) {
				const response = await fetch(url);
				return response.json();
			},
			$applyConditionConfigToDiagram(conditionConfig) {
				const diagramConfig = JSON.parse(JSON.stringify(this.diagram.model));
				const missingShapesInCondition = this.$getMissingShapesInCondition(diagramConfig, conditionConfig, components);

				Object.assign(conditionConfig, missingShapesInCondition);

				this.diagram.replaceState(conditionConfig);
			},
			$getMissingShapesInCondition(diagramConfig, conditionConfig, diagramShapes) {
				const conditionShapesIds = new Set(Object.keys(conditionConfig));
				const missingShapesInCondition = {};

				for (const diagramShapeId in diagramConfig) {
					if (conditionShapesIds.has(diagramShapeId)) continue;

					const domElement = document.getElementById(diagramShapeId);
					const masterId = domElement?.dataset?.master;
					const status = diagramShapes[masterId]?.model?.status?.default;

					// condition does not have component registered => do not assign last model status, set it to status: undefined
					missingShapesInCondition[diagramShapeId] = { status };
				}

				return missingShapesInCondition;
			},
			$handleConditionChange(condition, editMode) {
				const conditionChanged = this.current_condition_id !== '' && condition._id !== this.current_condition_id;

				if (conditionChanged && this.$shouldOpenLeftPanel(condition, editMode)) {
					this.leftPanelSelected = 0;
				}

				this.current_condition_id = condition._id;
				this.current_condition_changed = false;
			},
			$shouldOpenLeftPanel(condition, editMode) {
				const description = condition.metadata?.description;
				const isEmptyDescription = !description || description === '<p></p>';

				return editMode || !isEmptyDescription;
			},
			$addNewAttachmentFrom(file, name, type) {
				const existingAttachmentIndex = this.attachments.findIndex(attachment => attachment.name === name);
				if (existingAttachmentIndex > -1) this.attachments.splice(existingAttachmentIndex, 1);

				const newAttachmentToAdd = new Attachment(name, file, URL.createObjectURL(new Blob([file])), type);

				this.attachments.push(newAttachmentToAdd);
			},
			$normalizeDatasheetTag(tag) {
				const hasException = this.datasheetPrefixExceptionsRegexps.some(regexp => regexp.test(tag));

				return hasException ? tag : `${this.datasheetPrefix}${tag}`;
			},
			async $loadDatasheet(tag) {
				return PServiceContent.findContentByCustomId({
					type: 'datasheet',
					custom_id: tag,
					library_id: getLibraryId(this.value)
				})
					.then(([datasheet]) => {
						this.setNotes(false);
						this.datasheet = datasheet;
						this.$openRightDrawer();
					})
					.catch(() => {
						this.setNotes(NO_CONTENT_RELATED_MESSAGE);
						this.datasheet = false;
						this.$closeRightDrawer();
					});
			},
			$showShapeNotes(id) {
				const shape = this.shapes[id];

				if (!shape) {
					this.setNotes(NO_CONTENT_RELATED_MESSAGE);
					this.$openRightDrawer();
					return;
				}

				this.setTag(false);

				const notes = shape.notes;

				if (!notes) return;

				const sanitizedNotes = marked(`## Notes:\n\n${notes}`, { sanitize: true });
				this.setNotes(sanitizedNotes);
				this.$openRightDrawer();
			},
			$openRightDrawer() {
				if (!this.rightDrawerStatus) {
					this.toggleRightDrawer();
				}
			},
			$closeRightDrawer() {
				if (this.rightDrawerStatus) {
					this.toggleRightDrawer();
				}
			},
			$getCurrentView() {
				const diagramLayers = JSON.parse(JSON.stringify(this.diagram.layers ?? {}));
				const panZoom = { pan: this.pan, zoom: this.zoom };

				return {
					condition_id: this.current_condition_id,
					condition_title: this.current_condition?.metadata.title,
					datasheetFilter: this.datasheetFilter,
					highlighted: this.highlighted,
					isPanZoomCentered: this.isPanZoomCentered,
					layers: diagramLayers,
					leftDrawerIndex: this.leftPanelSelected,
					panZoom: panZoom,
					selected: this.$getIdsFromSelectedDiagramComponents()
				};
			},
			$getIdsFromSelectedDiagramComponents() {
				return [...document.querySelectorAll('[data-selectedId]')].map(element => element.dataset.selectedId);
			},
			$syncCondition(conditionId) {
				if (this.current_condition_id === conditionId) return;

				const condition = this.conditions.find(condition => condition._id === conditionId);

				if (condition) this.changeCondition(condition);
			},
			$syncLayers(layers) {
				const diagramControllerLayersStringified = JSON.stringify(this.diagram.layers);
				const viewLayersStringified = JSON.stringify(layers);

				if (diagramControllerLayersStringified !== viewLayersStringified) {
					this.$setDiagramLayers();
				}
			},
			$syncHighlighted(highlighted) {
				const oldHighlighted = JSON.stringify(this.highlighted);
				const newHighlighted = JSON.stringify(highlighted);

				if (oldHighlighted === newHighlighted || !highlighted) return;

				highlighted.forEach(id => {
					const svgComponentHighlighted = document.getElementById(id);

					if (svgComponentHighlighted) this.diagram.highlight(svgComponentHighlighted);
				});

				this.highlighted = highlighted;
			},
			$syncLeftPanel(leftDrawerIndex) {
				if (this.leftPanelSelected !== leftDrawerIndex) {
					this.leftPanelSelected = leftDrawerIndex;
				}
			},
			$syncPanZoom(panZoom, isPanZoomCentered) {
				if (!panZoom) return;

				try {
					if (isPanZoomCentered) {
						this.isResettingPanZoom = true;
						this.isPanZoomCentered = true;
					}

					if (JSON.stringify(panZoom.pan) !== JSON.stringify(this.pan)) {
						this.$setPan(panZoom.pan);
					}

					if (panZoom.zoom !== this.zoom) {
						this.$setZoom(panZoom.zoom);
						this.$setPan(panZoom.pan);
					}
				} finally {
					this.isResettingPanZoom = false;
				}
			},
			$emitCurrentView() {
				if (!this.isDiagramInitialized) return;

				vm.$emit('update:view', this.$getCurrentView());
			}
		},
		computed: {
			value() {
				return vm.value;
			},
			view() {
				return vm.view;
			},
			attachments() {
				return this.value.body.attachments;
			},
			settings() {
				return this.value.body.contents.settings;
			},
			shapes() {
				return this.value.body.contents.shapes;
			},
			svg_attachment() {
				return this.value.body.attachments.find(attachment => attachment.name === AttachmentName.svg);
			},
			vsdx_attachment() {
				return this.value.body.attachments.find(attachment => attachment.name === AttachmentName.vsdx);
			},
			conditions() {
				return this.value.body.attachments.filter(
					attachment => attachment.attachment_type === `.${AttachmentType.json}`
				);
			},
			current_condition() {
				return this.value.body.attachments.find(attachment => attachment._id === this.current_condition_id);
			},
			layers() {
				return this.diagram.layers;
			}
		},
		watch: {
			'view.condition_id'(conditionId) {
				this.$syncCondition(conditionId);
			},
			'view.layers'(layers) {
				this.$syncLayers(layers);
			},
			'view.highlighted'(highlighted) {
				this.$syncHighlighted(highlighted);
			},
			'view.leftDrawerIndex'(leftDrawerIndex) {
				this.$syncLeftPanel(leftDrawerIndex);
			},
			'view.panZoom': {
				handler(panZoom) {
					this.$syncPanZoom(panZoom, this.view.isPanZoomCentered);
				},
				deep: true
			},
			leftPanelSelected() {
				this.$emitCurrentView();
			},
			current_condition_id() {
				this.$emitCurrentView();
			},
			selected() {
				this.$emitCurrentView();
			},
			highlighted() {
				this.$emitCurrentView();
			},
			layers: {
				deep: true,
				handler() {
					this.$emitCurrentView();
				}
			},
			datasheetFilter: {
				deep: true,
				immediate: true,
				handler() {
					if (this.diagram) {
						this.$emitCurrentView();
					}
				}
			}
		}
	});
}
